import { applyMiddleware, Middleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';

import { ErrorItem } from '../../components/ErrorsPresenter/ErrorsPresenter';
import { ValidationErrorData } from '../../typings/common';
import { AdvType, AdvTypeClient } from '../../typings/domain';
import { getIsCpmAudio } from '../AddCreativePage/AddVideoCreative/helpers';

import { createCreativeSize } from './mappers';
import { ALL_SIZES, CreativeType, SharedData } from './typings';

const isProduction = process.env.NODE_ENV === 'production';

export function applyReduxMiddleware(middleware: Middleware) {
    return isProduction ? applyMiddleware(middleware) : composeWithDevTools(applyMiddleware(middleware));
}

export const getValidationErrorData = (message: string): ValidationErrorData => {
    return {
        text: message
    };
};

export const getErrorItems = (path: string, message: string): ErrorItem[] => {
    return [
        {
            path,
            text: message
        }
    ];
};

export function isVideoAddition(type?: CreativeType): boolean {
    return type === CreativeType.VIDEO_ADDITION;
}

export function isOverlay(type?: CreativeType): boolean {
    return type === CreativeType.OVERLAY;
}

export function isGraphicCreatives(type?: CreativeType): boolean {
    return type === CreativeType.COMMON || type === CreativeType.CPM_GEOPRODUCT || type === CreativeType.ADAPTIVE;
}

export function isCommonCreative(type?: CreativeType): boolean {
    return type === CreativeType.COMMON || type === CreativeType.CPM_GEOPRODUCT;
}

export function isAdaptiveCreative(type?: CreativeType): boolean {
    return type === CreativeType.ADAPTIVE;
}

export function findItem<T extends { id: string }>(id: string, items: T[]): T {
    const item = items.find(current => current.id === id);
    if (!item) {
        throw new Error(`Wrong id: ${id}`);
    }

    return item;
}

export function getSharedData(
    creativeType: CreativeType,
    clientId?: string,
    adType?: AdvType | AdvTypeClient,
    creativeId?: string
): SharedData {
    if (!adType) {
        return {
            clientId: String(clientId),
            creativeType,
            creativeId
        };
    }

    const additionalParams: Partial<SharedData> = {};

    switch (adType) {
        case AdvType.CPM_BANNER:
        case AdvType.CPM_VIDEO:
            additionalParams.cpm = 1;
            break;
        case AdvType.CPM_OUTDOOR:
            additionalParams.cpm_outdoor = 1;
            break;
        case AdvType.CPM_INDOOR:
            additionalParams.cpm_indoor = 1;
            break;
        case AdvType.CPM_AUDIO:
            additionalParams.cpm_audio = 1;
            break;
        default:
            additionalParams.cpc = 1;
            break;
    }

    return {
        clientId: String(clientId),
        creativeType,
        creativeId,
        ...additionalParams
    };
}

export function getPresetId(sharedData: SharedData): string | undefined {
    const isCpm = Boolean(sharedData.cpm);
    const isOutDoor = Boolean(sharedData.cpm_outdoor);
    const isInDoor = Boolean(sharedData.cpm_indoor);
    const isGeoProduct = Boolean(sharedData.cpm_geoproduct);
    const isCpmAudio = Boolean(sharedData.cpm_audio);
    const isAdaptive = Boolean(sharedData.isAdaptive);
    const isOverlayCreative = Boolean(sharedData.creativeType === CreativeType.OVERLAY);

    if (isOverlayCreative) {
        return '351';
    } else if (isCpm) {
        return '6';
    } else if (isOutDoor) {
        return '101';
    } else if (isInDoor) {
        return '201';
    } else if (isGeoProduct) {
        return '10';
    } else if (isCpmAudio) {
        return '301';
    } else if (isAdaptive) {
        return '5';
    } else {
        return undefined;
    }
}

export function isCreateImmediately(sharedData: SharedData): boolean {
    return (
        Boolean(sharedData.cpm) ||
        Boolean(sharedData.cpm_outdoor) ||
        Boolean(sharedData.cpm_indoor) ||
        getIsCpmAudio(sharedData) ||
        Boolean(sharedData.cpm_geoproduct) ||
        sharedData.creativeType === CreativeType.OVERLAY
    );
}

export function getCreativeSizes(sharedData: SharedData): string[] {
    if (sharedData.filters && sharedData.filters.sizes) {
        return sharedData.filters.sizes.map(size => createCreativeSize(size));
    }

    const creativeType =
        sharedData.creativeType === CreativeType.ADAPTIVE
            ? CreativeType.COMMON
            : sharedData.creativeType || CreativeType.COMMON;
    return ALL_SIZES[creativeType];
}
